import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, map, mergeMap, Observable, of, switchMap, throwError } from 'rxjs';

import { AwsService } from '../../../../core/services/aws.service';
import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { ApiResponse } from '../../interfaces/commons';
import { IAttachment } from '../../interfaces/IAttachment';
import { ChannelConsent, IChannel } from '../../interfaces/IChannel';
import { IMessage, IPartialMessage, IReaction } from '../../interfaces/IMessage';
import { MessagingApiStateService } from './messaging-api-state.service';

@Injectable()
export class MessagesApiService {
  constructor(
    private httpClient: HttpClient,
    private env: MessagingEnvironmentService,
    private state: MessagingApiStateService,
    private awsService: AwsService
  ) {}

  getMessages(channel: IChannel) {
    const filters = {};
    const lastMessage = channel.messages[0];
    const date = lastMessage?.createdAt ? new Date(lastMessage.createdAt).getTime() : new Date().getTime();
    const data: {
      searchAfter: number[];
      page: number;
      size: number;
      memberUID: string;
      fromDate?: Date;
      toDate?: Date;
      tags?: string[];
    } = {
      ...filters,
      memberUID: channel.member.uid,
      size: this.state.messagesFilter.size,
      page: 1,
      searchAfter: [date],
    };
    if (channel.messagesStartDate) {
      data.fromDate = channel.messagesStartDate;
    }
    if (channel.messagesEndDate) {
      data.toDate = channel.messagesEndDate;
    }

    return this.httpClient
      .post<ApiResponse<IMessage>>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/search`, data)
      .pipe(
        mergeMap((e) => {
          e.documents = e.documents.reverse();
          return of(e);
        })
      );
  }

  sendMessage(message: IPartialMessage): Observable<IMessage> {
    return this.awsService.uploadFiles(message.attachments).pipe(
      catchError((error: { status?: number; message?: string }) =>
        throwError(() => ({
          status: error?.status,
          errorDescription: error?.message,
          isUploadError: true,
        }))
      ),
      switchMap((attachments) => {
        message.attachments = attachments;
        const messageRequest: {
          memberUID: string;
          message: string;
          tags: string[];
          attachments?: IAttachment[];
          templateId?: ChannelConsent;
        } = {
          memberUID: message.channel.member.uid,
          message: message.message || '',
          tags: message.systemTags ?? [],
        };
        if (message.attachments.length) {
          messageRequest.attachments = message.attachments;
        }

        if (message.template) {
          messageRequest.templateId = message.template.externalId;
        }
        return this.httpClient
          .post<IMessage>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/send`, messageRequest)
          .pipe(
            map((messageRes) => {
              messageRes.attachments = messageRequest.attachments;
              return messageRes;
            })
          );
      })
    );
  }

  checkNewMessagesByDate(channel: IChannel, date: Date): Observable<boolean> {
    if (!date) {
      return of(false);
    }
    const data = {
      fromDate: new Date(date.getTime() + 1),
      page: 1,
      size: 1,
      memberUID: channel.member.uid,
    };
    return this.httpClient
      .post<ApiResponse<IMessage>>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/search`, data)
      .pipe(
        map((e) => {
          return e.documents.length > 0;
        })
      );
  }

  deleteMessageById(messageUid: string): Observable<unknown> {
    return this.httpClient.delete(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/${messageUid}`);
  }

  changeMessageDeliveryStatus(messageUID: string, _value: string): Observable<unknown> {
    return this.httpClient.patch(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/${messageUID}`, {
      messageableEntityUID: this.state.user?.subjectMessageableEntity?.uid ?? '',
    });
  }

  sendReaction(reaction: IReaction, channelUID: string, messageUID: string): Observable<unknown> {
    return this.httpClient.post(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/${messageUID}/reaction`, {
      channelUID,
      reaction: {
        body: reaction.body,
      },
    });
  }

  getChannelOldMessagesValue(channel: IChannel, startDate: Date): Observable<boolean> {
    if (!startDate) {
      return of(false);
    }
    const data = {
      toDate: startDate,
      page: 1,
      size: 1,
      memberUID: channel.member.uid,
    };
    return this.httpClient
      .post<ApiResponse<IMessage>>(`${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/search`, data)
      .pipe(
        map((e) => {
          return e.documents.length > 0;
        })
      );
  }

  notifyVideoCallGroupMessage(memberUID: string, message: string) {
    const messageRequest = {
      memberUID: memberUID,
      message,
    };
    return this.httpClient.post<IMessage>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/message/send`,
      messageRequest
    );
  }
}
